import { useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  CreditCard, Wallet, RefreshCw, Download, Filter, ChevronRight,
  Building2, Users, Percent, ArrowRightLeft
} from "lucide-react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.05 } },
};

const revenueByMonth = [
  { month: "Oct", gross: 24500, commission: 3675, netPayout: 20825 },
  { month: "Nov", gross: 29800, commission: 4470, netPayout: 25330 },
  { month: "Dec", gross: 38200, commission: 5730, netPayout: 32470 },
  { month: "Jan", gross: 41500, commission: 6225, netPayout: 35275 },
  { month: "Feb", gross: 47200, commission: 7080, netPayout: 40120 },
  { month: "Mar", gross: 56400, commission: 8460, netPayout: 47940 },
];

const payoutsByProvider = [
  { provider: "Amara Beauty Studio", amount: "$4,820", status: "completed", date: "Mar 28, 2026", method: "Bank Transfer" },
  { provider: "Zen Wellness Spa", amount: "$3,540", status: "completed", date: "Mar 28, 2026", method: "Bank Transfer" },
  { provider: "Urban Barber Co.", amount: "$2,980", status: "pending", date: "Mar 30, 2026", method: "Bank Transfer" },
  { provider: "Ink & Soul Tattoos", amount: "$2,450", status: "pending", date: "Mar 30, 2026", method: "Mobile Money" },
  { provider: "Glow Nails Lounge", amount: "$1,680", status: "failed", date: "Mar 27, 2026", method: "Bank Transfer" },
];

const transactions = [
  { id: "TXN-4521", type: "booking", customer: "Emily Watson", provider: "Amara Beauty Studio", service: "Hair Coloring", amount: "$85.00", commission: "$12.75", date: "Mar 30, 2026", status: "completed" },
  { id: "TXN-4520", type: "booking", customer: "James Miller", provider: "Urban Barber Co.", service: "Haircut & Beard", amount: "$45.00", commission: "$6.75", date: "Mar 30, 2026", status: "completed" },
  { id: "TXN-4519", type: "refund", customer: "Lisa Chang", provider: "Zen Wellness Spa", service: "Deep Tissue Massage", amount: "-$120.00", commission: "-$18.00", date: "Mar 29, 2026", status: "refunded" },
  { id: "TXN-4518", type: "booking", customer: "Sarah Kim", provider: "Glow Nails Lounge", service: "Gel Manicure", amount: "$55.00", commission: "$8.25", date: "Mar 29, 2026", status: "completed" },
  { id: "TXN-4517", type: "booking", customer: "Omar Hassan", provider: "Ink & Soul Tattoos", service: "Custom Tattoo", amount: "$250.00", commission: "$37.50", date: "Mar 28, 2026", status: "completed" },
  { id: "TXN-4516", type: "payout", customer: "—", provider: "Amara Beauty Studio", service: "Weekly Payout", amount: "-$4,820.00", commission: "—", date: "Mar 28, 2026", status: "completed" },
];

const commissionTiers = [
  { tier: "Standard", rate: "15%", providers: 280, revenue: "$32,400" },
  { tier: "Premium", rate: "12%", providers: 45, revenue: "$9,800" },
  { tier: "Enterprise", rate: "10%", providers: 12, revenue: "$5,600" },
  { tier: "New Provider", rate: "10% (promo)", providers: 5, revenue: "$620" },
];

const financeStats = [
  { label: "Gross Revenue", value: "$56,400", change: "+19.5%", up: true, icon: DollarSign, color: "bg-primary/10 text-primary" },
  { label: "Platform Commission", value: "$8,460", change: "+19.5%", up: true, icon: Percent, color: "bg-secondary/10 text-secondary-foreground" },
  { label: "Provider Payouts", value: "$47,940", change: "+19.4%", up: true, icon: Building2, color: "bg-accent/10 text-accent-foreground" },
  { label: "Pending Payouts", value: "$5,430", change: "-12.3%", up: false, icon: Wallet, color: "bg-olive/10 text-olive" },
];

const statusColor = (status: string) => {
  switch (status) {
    case "completed": return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "pending": return "bg-amber-50 text-amber-700 border-amber-200";
    case "failed": case "refunded": return "bg-red-50 text-red-700 border-red-200";
    default: return "bg-muted text-muted-foreground";
  }
};

type FinanceTab = "overview" | "transactions" | "payouts" | "commissions";

const AdminFinances = () => {
  const [activeTab, setActiveTab] = useState<FinanceTab>("overview");
  const [txnFilter, setTxnFilter] = useState("all");

  const filteredTxns = txnFilter === "all" ? transactions : transactions.filter(t => t.type === txnFilter);

  const tabs: { key: FinanceTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "transactions", label: "Transactions" },
    { key: "payouts", label: "Payouts" },
    { key: "commissions", label: "Commissions" },
  ];

  return (
    <motion.div key="finances" {...fadeIn}>
      {/* Sub-tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex rounded-lg border border-border overflow-hidden">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-3 py-1.5 text-xs font-medium ${
                activeTab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
          <Download className="h-3 w-3" /> Export
        </Button>
      </div>

      {activeTab === "overview" && (
        <div>
          {/* Stats */}
          <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" variants={stagger} initial="initial" animate="animate">
            {financeStats.map(({ label, value, change, up, icon: Icon, color }) => (
              <motion.div key={label} variants={fadeIn} className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-lg ${color}`}><Icon className="h-4 w-4" /></div>
                  <div className={`flex items-center gap-0.5 text-xs font-medium ${up ? "text-emerald-600" : "text-red-500"}`}>
                    {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {change}
                  </div>
                </div>
                <p className="text-2xl font-bold text-foreground">{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Revenue chart */}
          <div className="rounded-xl border border-border bg-background p-4 mb-4">
            <h4 className="font-semibold text-foreground mb-4">Revenue Breakdown</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueByMonth} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(44, 30%, 85%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(0, 0%, 100%)", border: "1px solid hsl(44, 30%, 85%)", borderRadius: "8px", fontSize: "12px" }} formatter={(value: number) => [`$${value.toLocaleString()}`, ""]} />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                  <Bar dataKey="netPayout" name="Provider Payouts" fill="hsl(60, 13%, 57%)" radius={[4, 4, 0, 0]} barSize={20} />
                  <Bar dataKey="commission" name="Commission" fill="hsl(357, 25%, 63%)" radius={[4, 4, 0, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent transactions quick list */}
          <div className="rounded-xl border border-border bg-background">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h4 className="font-semibold text-foreground">Recent Transactions</h4>
              <Button variant="ghost" size="sm" className="text-primary text-xs" onClick={() => setActiveTab("transactions")}>
                View All <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
            <div className="divide-y divide-border">
              {transactions.slice(0, 4).map(t => (
                <div key={t.id} className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${t.type === "refund" ? "bg-red-50" : t.type === "payout" ? "bg-amber-50" : "bg-emerald-50"}`}>
                      {t.type === "refund" ? <RefreshCw className="h-4 w-4 text-red-600" /> : t.type === "payout" ? <ArrowRightLeft className="h-4 w-4 text-amber-600" /> : <CreditCard className="h-4 w-4 text-emerald-600" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{t.service}</p>
                      <p className="text-xs text-muted-foreground">{t.provider} · {t.date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${t.amount.startsWith("-") ? "text-red-600" : "text-foreground"}`}>{t.amount}</p>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusColor(t.status)}`}>{t.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "transactions" && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex rounded-lg border border-border overflow-hidden">
              {["all", "booking", "refund", "payout"].map(f => (
                <button key={f} onClick={() => setTxnFilter(f)} className={`px-3 py-1.5 text-xs font-medium capitalize ${txnFilter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-background overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">ID</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Service</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Customer</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Provider</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Commission</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredTxns.map(t => (
                    <tr key={t.id} className="hover:bg-muted/20">
                      <td className="px-4 py-3 text-muted-foreground text-xs">{t.id}</td>
                      <td className="px-4 py-3"><Badge variant="secondary" className="text-[10px] capitalize">{t.type}</Badge></td>
                      <td className="px-4 py-3 font-medium text-foreground hidden md:table-cell">{t.service}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">{t.customer}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{t.provider}</td>
                      <td className={`px-4 py-3 text-right font-medium ${t.amount.startsWith("-") ? "text-red-600" : "text-foreground"}`}>{t.amount}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground hidden md:table-cell">{t.commission}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusColor(t.status)}`}>{t.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === "payouts" && (
        <div>
          {/* Payout summary */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border border-border bg-background p-4 text-center">
              <p className="text-2xl font-bold text-foreground">$15,470</p>
              <p className="text-xs text-muted-foreground mt-1">Paid This Month</p>
            </div>
            <div className="rounded-xl border border-border bg-background p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">$5,430</p>
              <p className="text-xs text-muted-foreground mt-1">Pending</p>
            </div>
            <div className="rounded-xl border border-border bg-background p-4 text-center">
              <p className="text-2xl font-bold text-red-600">$1,680</p>
              <p className="text-xs text-muted-foreground mt-1">Failed</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h4 className="font-semibold text-foreground">Provider Payouts</h4>
              <Button size="sm" className="h-8 text-xs gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                <ArrowRightLeft className="h-3 w-3" /> Process All Pending
              </Button>
            </div>
            <div className="divide-y divide-border">
              {payoutsByProvider.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{p.provider}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span>{p.method}</span>
                      <span>{p.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-foreground">{p.amount}</p>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusColor(p.status)}`}>{p.status}</span>
                    {p.status === "failed" && (
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1">
                        <RefreshCw className="h-3 w-3" /> Retry
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "commissions" && (
        <div>
          {/* Commission overview */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl border border-border bg-background p-4">
              <Percent className="h-5 w-5 text-primary mb-2" />
              <p className="text-2xl font-bold text-foreground">15%</p>
              <p className="text-xs text-muted-foreground mt-0.5">Default Commission Rate</p>
            </div>
            <div className="rounded-xl border border-border bg-background p-4">
              <DollarSign className="h-5 w-5 text-primary mb-2" />
              <p className="text-2xl font-bold text-foreground">$8,460</p>
              <p className="text-xs text-muted-foreground mt-0.5">Total Commission (Mar)</p>
            </div>
          </div>

          {/* Commission chart */}
          <div className="rounded-xl border border-border bg-background p-4 mb-4">
            <h4 className="font-semibold text-foreground mb-4">Commission Trend</h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueByMonth}>
                  <defs>
                    <linearGradient id="commGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(357, 25%, 63%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(357, 25%, 63%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(44, 30%, 85%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(60, 13%, 40%)" tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(0, 0%, 100%)", border: "1px solid hsl(44, 30%, 85%)", borderRadius: "8px", fontSize: "12px" }} formatter={(value: number) => [`$${value.toLocaleString()}`, "Commission"]} />
                  <Area type="monotone" dataKey="commission" stroke="hsl(357, 25%, 63%)" fill="url(#commGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tiers table */}
          <div className="rounded-xl border border-border bg-background p-4">
            <h4 className="font-semibold text-foreground mb-3">Commission Tiers</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 font-medium text-muted-foreground">Tier</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Rate</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Providers</th>
                    <th className="text-right py-2.5 font-medium text-muted-foreground">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {commissionTiers.map(c => (
                    <tr key={c.tier} className="hover:bg-muted/20">
                      <td className="py-2.5 font-medium text-foreground">{c.tier}</td>
                      <td className="py-2.5 text-right text-primary font-semibold">{c.rate}</td>
                      <td className="py-2.5 text-right text-muted-foreground">{c.providers}</td>
                      <td className="py-2.5 text-right font-medium text-foreground">{c.revenue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminFinances;
